import { useEffect, useState } from 'react';
import { runtimeApi, type ModuleManifest, type ModuleRecord } from '../../api/moduleruntime';

export function ModuleManagerPage() {
  const [available, setAvailable] = useState<ModuleManifest[]>([]);
  const [installed, setInstalled] = useState<ModuleRecord[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const refresh = () => {
    void runtimeApi.listModules().then((r) => {
      setAvailable(r.available ?? []);
      setInstalled(r.installed ?? []);
    }).catch((e) => setError(String(e)));
  };

  useEffect(refresh, []);

  const installedIds = new Set(installed.map((m) => m.module_id));

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError('');
    try {
      await fn();
      refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Module Manager</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Install, enable, disable and manage enterprise modules.</p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {busy && <p className="mt-2 text-sm text-slate-500">Working: {busy}…</p>}
      <div className="mt-6 overflow-x-auto rounded-xl border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Module</th>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Version</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {available.map((m) => {
              const rec = installed.find((i) => i.module_id === m.id);
              return (
                <tr key={m.id} className="border-t dark:border-slate-700">
                  <td className="px-4 py-2">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.id}</div>
                  </td>
                  <td className="px-4 py-2">{m.category}</td>
                  <td className="px-4 py-2">{m.version}</td>
                  <td className="px-4 py-2">{rec?.state ?? 'not_installed'}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {!installedIds.has(m.id) && (
                        <button type="button" className="rounded bg-slate-900 px-2 py-1 text-xs text-white dark:bg-slate-100 dark:text-slate-900" onClick={() => act(`install-${m.id}`, () => runtimeApi.install(m.id))}>Install</button>
                      )}
                      {rec && rec.state !== 'running' && rec.state !== 'enabled' && (
                        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => act(`enable-${m.id}`, () => runtimeApi.enable(m.id))}>Enable</button>
                      )}
                      {(rec?.state === 'running' || rec?.state === 'enabled') && (
                        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => act(`disable-${m.id}`, () => runtimeApi.disable(m.id))}>Disable</button>
                      )}
                      {rec && (
                        <button type="button" className="rounded border px-2 py-1 text-xs text-red-700" onClick={() => act(`uninstall-${m.id}`, () => runtimeApi.uninstall(m.id))}>Uninstall</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
